import * as PIXI from 'pixi.js';

import type Luminary from './';

type PooledTexture = {
    texture: PIXI.Texture;
    canvas: HTMLCanvasElement;
    holder: object | undefined;
    pixelsDrawnBy: object | undefined;
    detach: () => void;
};

export class CanvasReusableTexturesManager {
    #luminary: Luminary;
    #unusedTextures: PooledTexture[] = [];
    #texturesInUse: PooledTexture[] = [];

    constructor(luminary: Luminary) {
        this.#luminary = luminary;
    }

    allocateTexture(detach: () => void, holder?: object): PIXI.Texture {
        const pooled = this.#unusedTextures.pop() ?? this.#createTexture();
        if (holder !== undefined && pooled.pixelsDrawnBy !== holder) {
            const context = pooled.canvas.getContext('2d');
            if (!context) {
                throw new Error('Pooled canvas has been set to a different context mode');
            }
            context.resetTransform();
            context.clearRect(0, 0, pooled.canvas.width, pooled.canvas.height);
            pooled.pixelsDrawnBy = undefined;
        }
        pooled.holder = holder;
        pooled.detach = detach;
        this.#texturesInUse.push(pooled);
        return pooled.texture;
    }

    #createTexture(): PooledTexture {
        const canvas = document.createElement('canvas');
        const canvasResolution = this.#luminary.outputResolution;
        canvas.width = canvasResolution.width;
        canvas.height = canvasResolution.height;
        canvas.style.width = this.#luminary.outputResolution.width + 'px';
        canvas.style.height = this.#luminary.outputResolution.height + 'px';
        const texture = PIXI.Texture.from(canvas);
        texture.baseTexture.setSize(
            this.#luminary.referenceResolution.width,
            this.#luminary.referenceResolution.height,
        );
        return { texture, canvas, holder: undefined, pixelsDrawnBy: undefined, detach: () => {} };
    }

    detachTextures() {
        for (const pooled of this.#texturesInUse) {
            pooled.detach();
            pooled.pixelsDrawnBy = pooled.holder;
            pooled.holder = undefined;

            // destroy() runs this after assets (and their textures) are torn down; a destroyed
            // texture can't be re-framed or recycled.
            if (pooled.texture.destroyed) {
                continue;
            }

            const textureWidth = this.#luminary.referenceResolution.width;
            const textureHeight = this.#luminary.referenceResolution.height;
            const rectangle = new PIXI.Rectangle(0, 0, textureWidth, textureHeight);
            pooled.texture.orig = rectangle;
            pooled.texture.frame = rectangle;
            pooled.texture.trim = rectangle;
            this.#unusedTextures.unshift(pooled);
        }
        this.#texturesInUse = [];
    }

    destroy() {
        this.detachTextures();
        for (const pooled of this.#unusedTextures) {
            pooled.texture.destroy(true);
        }

        this.#unusedTextures = [];
    }

    getDebugInfo() {
        return {
            texturesInUse: this.#texturesInUse.length,
            unusedTextures: this.#unusedTextures.length,
        };
    }
}
